import React from 'react';
import { CompetitorData } from '../../types/dataTypes';

interface SeoRankingChartProps {
  data: CompetitorData;
}

const SeoRankingChart: React.FC<SeoRankingChartProps> = ({ data }) => {
  const competitors = ['You', ...data.competitors.map(c => c.name)];
  
  const categories = [
    { name: 'Top 3', color: 'bg-green-500' },
    { name: '4-10', color: 'bg-blue-500' },
    { name: '11-20', color: 'bg-yellow-500' },
    { name: '21-50', color: 'bg-orange-500' },
    { name: '51-100', color: 'bg-red-500' }
  ];
  
  // Mock distribution data for rankings
  const distributions = [
    [25, 30, 20, 15, 10], // You
    [15, 25, 30, 20, 10], // Competitor 1
    [10, 20, 25, 25, 20], // Competitor 2
    [5, 15, 25, 30, 25]   // Competitor 3
  ];
  
  return (
    <div>
      <div className="mb-4 flex items-center justify-end space-x-4">
        {categories.map((category, idx) => (
          <div key={idx} className="flex items-center">
            <div className={`h-3 w-3 ${category.color} rounded-sm mr-1`}></div>
            <span className="text-xs text-gray-500 dark:text-gray-400">{category.name}</span>
          </div>
        ))}
      </div>
      
      <div className="space-y-6">
        {competitors.map((competitor, compIdx) => {
          const distribution = distributions[compIdx];
          return (
            <div key={compIdx} className="space-y-1">
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {competitor}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {data.seoMetrics.organicKeywords} keywords
                </span>
              </div>
              
              <div className="flex h-6 rounded-md overflow-hidden">
                {distribution.map((value, idx) => (
                  <div 
                    key={idx}
                    className={`${categories[idx].color} transition-all duration-500 relative group`}
                    style={{ width: `${value}%` }}
                  >
                    <div className="opacity-0 group-hover:opacity-100 absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap transition-opacity duration-200">
                      {value}% in {categories[idx].name}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Ranking Insights:</h4>
        <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
          <li className="flex items-start">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-500 mt-1.5 mr-2"></span>
            Your site has stronger rankings for top positions compared to competitors
          </li>
          <li className="flex items-start">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-500 mt-1.5 mr-2"></span>
            Competitor 2 has more keywords ranking between positions 11-50
          </li>
          <li className="flex items-start">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-500 mt-1.5 mr-2"></span>
            Opportunity to improve rankings for keywords in positions 21-50
          </li>
        </ul>
      </div>
    </div>
  );
};

export default SeoRankingChart;